import { useParams } from "react-router";
import { useFormatter, useLocale, useTranslations } from "use-intl";
import { lt } from "@liftquest/shared/localized";
import { Page, PageHeader, Section } from "@/components/app/page-header";
import { NotFound, PageLoading } from "@/components/app/states";
import { Card } from "@/components/ui/card";
import { useExerciseHistory } from "@/lib/queries";
import { ProgressChart } from "./progress-chart";

export function ExerciseProgressPage() {
  const { exerciseId = "" } = useParams();
  const locale = useLocale();
  const t = useTranslations("progress");
  const format = useFormatter();
  const query = useExerciseHistory(exerciseId);
  if (query.isPending) return <PageLoading />;
  if (!query.data) return <NotFound />;
  const ex = query.data.exercise;
  const rows = query.data.history.map((r) => ({ ...r, date: new Date(r.date) }));

  const points = rows.map((r) => ({
    date: r.date.toISOString(),
    label: format.dateTime(r.date, { day: "numeric", month: "short" }),
    weight: r.maxWeight,
    oneRm: Math.round(r.best1rm * 10) / 10,
    volume: Math.round(r.volume),
    seconds: r.maxDuration,
    minutes: Math.round(r.maxDuration / 60),
  }));
  const best = ex.type === "reps" ? Math.max(0, ...rows.map((r) => r.maxWeight)) : Math.max(0, ...rows.map((r) => r.maxDuration));
  const best1rm = Math.max(0, ...rows.map((r) => r.best1rm));

  return (
    <Page>
      <PageHeader backHref="/progress" title={lt(ex.name, locale)} subtitle={t("exerciseProgress")} />
      {rows.length === 0 ? (
        <p className="mt-6 rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
          {t("noExerciseData")}
        </p>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-2 gap-2">
            <Card className="gap-0 px-4 py-3">
              <p className="text-2xl font-bold tabular-nums">
                {ex.type === "reps"
                  ? `${format.number(best)} kg`
                  : ex.type === "time"
                    ? `${best} s`
                    : `${Math.round(best / 60)} min`}
              </p>
              <p className="text-xs text-muted-foreground">{ex.type === "reps" ? t("bestWeight") : t("bestTime")}</p>
            </Card>
            {ex.type === "reps" && (
              <Card className="gap-0 px-4 py-3">
                <p className="text-2xl font-bold tabular-nums">
                  {format.number(best1rm, { maximumFractionDigits: 1 })} kg
                </p>
                <p className="text-xs text-muted-foreground">{t("est1rm")}</p>
              </Card>
            )}
          </div>
          <ProgressChart type={ex.type} points={points} />
          <Section title={t("history")}>
            <table className="w-full overflow-hidden rounded-2xl border bg-card text-sm">
              <tbody className="divide-y">
                {[...rows].reverse().map((r) => (
                  <tr key={r.sessionId}>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {format.dateTime(r.date, { dateStyle: "medium" })}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                      {ex.type === "reps"
                        ? `${format.number(r.maxWeight)} kg · 1RM ${format.number(r.best1rm, { maximumFractionDigits: 1 })}`
                        : ex.type === "time"
                          ? `${r.maxDuration} s`
                          : `${Math.round(r.maxDuration / 60)} min${r.distance ? ` · ${format.number(r.distance)} km` : ""}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </Page>
  );
}
